import { nomeDoMes } from '../relatorio/mes.js';
import { normalizarTexto } from '../texto/normalizar.js';
const HORA_PADRAO = 9;
const NOMES_DOS_DIAS = [
    'domingo',
    'segunda-feira',
    'terça-feira',
    'quarta-feira',
    'quinta-feira',
    'sexta-feira',
    'sábado',
];
// Chaves sem acento: é assim que o texto chega depois de normalizado, e o
// teclado do iPhone no bolso raramente acentua.
const DIAS_DA_SEMANA = {
    domingo: 0,
    segunda: 1,
    terca: 2,
    quarta: 3,
    quinta: 4,
    sexta: 5,
    sabado: 6,
};
// "9h", "10:30", "10h30" ou só "10". O `h` solto no fim cobre "10h".
const HORA = String.raw `(\d{1,2})(?:[h:](\d{2}))?h?`;
const COM_HORA = String.raw `(?:\s+(?:as\s+)?${HORA})?`;
const AMANHA = new RegExp(String.raw `^amanha${COM_HORA}$`);
const DIA_DA_SEMANA = new RegExp(String.raw `^(domingo|segunda|terca|quarta|quinta|sexta|sabado)(-feira)?${COM_HORA}$`);
const DAQUI_A = /^em\s+(\d{1,3})\s+(dias?|semanas?|(?:mes|meses))$/;
const DATA = new RegExp(String.raw `^(\d{1,2})/(\d{1,2})${COM_HORA}$`);
/** `undefined` nos grupos vira 9h; hora fora da faixa invalida tudo. */
function lerHora(hora, minuto) {
    if (hora === undefined)
        return { hora: HORA_PADRAO, minuto: 0 };
    const h = Number(hora);
    const m = minuto === undefined ? 0 : Number(minuto);
    if (h > 23 || m > 59)
        return null;
    return { hora: h, minuto: m };
}
function horaEmTexto({ hora, minuto }) {
    return minuto === 0
        ? `${hora}h`
        : `${hora}h${String(minuto).padStart(2, '0')}`;
}
/** "sábado, 14 de março, às 10h30"; o ano só aparece quando não é o de agora. */
function descreverData(alvo, agora, hora) {
    const dia = alvo.getDate();
    const diaEmTexto = dia === 1 ? '1º' : String(dia);
    const ano = alvo.getFullYear() === agora.getFullYear()
        ? ''
        : ` de ${alvo.getFullYear()}`;
    return `${NOMES_DOS_DIAS[alvo.getDay()]}, ${diaEmTexto} de ${nomeDoMes(alvo.getMonth() + 1)}${ano}, às ${horaEmTexto(hora)}`;
}
/** A frase relativa que o usuário digitou, com a data a que ela chegou entre
 *  parênteses: "em 2 semanas (sábado, 21 de março, às 12h)".
 *
 *  As duas metades servem a leitores diferentes e nenhuma dispensa a outra: a
 *  frase relativa é a que a pessoa reconhece como o que ela pediu, e a data
 *  resolvida é a que continua verdadeira daqui a um mês, quando alguém abrir
 *  o histórico da visita. */
function comAData(frase, alvo, agora, hora) {
    return `${frase} (${descreverData(alvo, agora, hora)})`;
}
/** A hora do dia a que uma data chegou — o que "em 2 dias" herda de agora. */
function horaDe(alvo) {
    return { hora: alvo.getHours(), minuto: alvo.getMinutes() };
}
function emDias(agora, dias, hora) {
    return new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + dias, hora.hora, hora.minuto, 0, 0);
}
/** Soma meses grudando no último dia quando o destino é mais curto (31/01 + 1 mês = 28/02). */
function somarMeses(agora, meses) {
    const alvo = new Date(agora.getTime());
    const dia = alvo.getDate();
    alvo.setDate(1);
    alvo.setMonth(alvo.getMonth() + meses);
    const ultimoDia = new Date(alvo.getFullYear(), alvo.getMonth() + 1, 0).getDate();
    alvo.setDate(Math.min(dia, ultimoDia));
    return alvo;
}
function interpretarAmanha(texto, agora) {
    const m = AMANHA.exec(texto);
    if (m === null)
        return undefined;
    const hora = lerHora(m[1], m[2]);
    if (hora === null)
        return null;
    const alvo = emDias(agora, 1, hora);
    return {
        em: alvo.toISOString(),
        // Sem a hora na frase da frente: ela já está entre parênteses, e
        // "amanhã às 10h (quarta-feira, 11 de março, às 10h)" diria duas vezes a
        // mesma coisa.
        descricao: comAData('amanhã', alvo, agora, hora),
    };
}
function interpretarDiaDaSemana(texto, agora) {
    const m = DIA_DA_SEMANA.exec(texto);
    if (m === null)
        return undefined;
    const alvoDaSemana = DIAS_DA_SEMANA[m[1] ?? ''];
    if (alvoDaSemana === undefined)
        return null;
    // "-feira" só existe de segunda a sexta; "sábado-feira" é engano, não pedido.
    if (m[2] !== undefined && (alvoDaSemana === 0 || alvoDaSemana === 6))
        return null;
    const hora = lerHora(m[3], m[4]);
    if (hora === null)
        return null;
    // Pedir "terça" numa terça quer dizer a próxima, nunca hoje.
    const faltam = (alvoDaSemana - agora.getDay() + 7) % 7 || 7;
    const alvo = emDias(agora, faltam, hora);
    return {
        em: alvo.toISOString(),
        descricao: descreverData(alvo, agora, hora),
    };
}
function interpretarDaquiA(texto, agora) {
    const m = DAQUI_A.exec(texto);
    if (m === null)
        return undefined;
    const quantidade = Number(m[1]);
    const unidade = m[2] ?? '';
    // "em 0 dias" não é uma data futura: é um engano de digitação.
    if (quantidade < 1)
        return null;
    // O horário de agora é mantido: quem diz "em 2 dias" quer esta hora.
    if (unidade.startsWith('dia')) {
        const alvo = new Date(agora.getTime());
        alvo.setDate(alvo.getDate() + quantidade);
        const frase = `em ${quantidade} ${quantidade === 1 ? 'dia' : 'dias'}`;
        return {
            em: alvo.toISOString(),
            descricao: comAData(frase, alvo, agora, horaDe(alvo)),
        };
    }
    if (unidade.startsWith('semana')) {
        const alvo = new Date(agora.getTime());
        alvo.setDate(alvo.getDate() + quantidade * 7);
        const frase = `em ${quantidade} ${quantidade === 1 ? 'semana' : 'semanas'}`;
        return {
            em: alvo.toISOString(),
            descricao: comAData(frase, alvo, agora, horaDe(alvo)),
        };
    }
    const alvo = somarMeses(agora, quantidade);
    const frase = `em ${quantidade} ${quantidade === 1 ? 'mês' : 'meses'}`;
    return {
        em: alvo.toISOString(),
        descricao: comAData(frase, alvo, agora, horaDe(alvo)),
    };
}
function interpretarData(texto, agora) {
    const m = DATA.exec(texto);
    if (m === null)
        return undefined;
    const dia = Number(m[1]);
    const mes = Number(m[2]);
    if (dia < 1 || dia > 31 || mes < 1 || mes > 12)
        return null;
    const hora = lerHora(m[3], m[4]);
    if (hora === null)
        return null;
    // Sem ano, o mais próximo que ainda não passou. O laço também descarta
    // 29/02 fora de ano bissexto: a data volta rolada e é rejeitada.
    for (let ano = agora.getFullYear(); ano <= agora.getFullYear() + 8; ano++) {
        const alvo = new Date(ano, mes - 1, dia, hora.hora, hora.minuto, 0, 0);
        if (alvo.getDate() !== dia || alvo.getMonth() !== mes - 1)
            continue;
        if (alvo.getTime() < agora.getTime())
            continue;
        return {
            em: alvo.toISOString(),
            descricao: descreverData(alvo, agora, hora),
        };
    }
    return null;
}
/**
 * Entende "amanhã", "sábado 10h", "sábado às 10:30", "em 2 dias",
 * "em 3 semanas", "em 1 mês", "15/03" e "15/03 14h", com ou sem acento.
 * Sem hora no texto, marca às 9h. Qualquer dúvida devolve `null`.
 *
 * A `descricao` **sempre** diz a data a que o texto chegou, mesmo quando o
 * que se digitou foi relativo: "em 2 semanas" vira "em 2 semanas (sábado, 21
 * de março, às 12h)". Ver a nota em `ProximaVisita.descricao`.
 *
 * "hoje" é recusado de propósito: sem hora ele marcaria 9h, que às 20h já é
 * passado, e agendar para trás é pior do que não agendar. Data com ano
 * ("15/03/2027") também não entra por ora — se a pré-visualização de
 * Registrar disser "não entendi" com frequência, é o primeiro candidato.
 */
export function interpretarProximaVisita(texto, agora) {
    const limpo = normalizarTexto(texto);
    if (limpo === '')
        return null;
    for (const interpretar of [
        interpretarAmanha,
        interpretarDiaDaSemana,
        interpretarDaquiA,
        interpretarData,
    ]) {
        const r = interpretar(limpo, agora);
        // `undefined` é "esta forma não é comigo"; `null` é "é comigo e está errado".
        if (r !== undefined)
            return r;
    }
    return null;
}
//# sourceMappingURL=proximaVisita.js.map